// ─── 🌧️ Campo non utilizzabile ───────────────────────────────────────────────
//
// Dal mockup approvato il 25/09/2026 (sezione 8), regola R1 del documento: un
// guasto, il maltempo, un evento. Si scelgono i campi e le ore, si vedono le
// prenotazioni coinvolte, e con un tocco si annullano tutte: chi era in campo
// riceve l'avviso nel bot. (🔄 30/09/2026: accanto c'era l'acconto di ciascuna,
// che tornava nel borsellino; l'acconto non c'è più.)

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type Campo } from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { Guasto } from '../../components/pezzi.js';
import { Pannello, TestaPannello } from './Pannello.js';

type Colpita = {
  bookingId: string;
  courtName: string;
  time: string;
  endTime: string;
  type: string;
  organizer: string | null;
};

export function CampoInagibile({
  data,
  campi,
  chiudi,
}: {
  data: string;
  campi: Campo[];
  chiudi(): void;
}): React.JSX.Element {
  const qc = useQueryClient();
  const [scelti, setScelti] = useState<string[]>([]);
  const [giorno, setGiorno] = useState(data);
  const [dalle, setDalle] = useState('18:00');
  const [alle, setAlle] = useState('23:30');
  const [motivo, setMotivo] = useState('');
  const [colpite, setColpite] = useState<Colpita[] | null>(null);
  const [fatto, setFatto] = useState<number | null>(null);

  const corpo = () => ({ courtIds: scelti, date: giorno, from: dalle, to: alle, reason: motivo.trim() });
  const pronto = scelti.length > 0 && motivo.trim().length >= 3 && dalle < alle;

  const guarda = useMutation({
    meta: { fatto: () => null },
    mutationFn: () => chiama<{ items: Colpita[] }>('POST', '/courts/unusable/preview', corpo()),
    onSuccess: (r) => setColpite(r.items),
  });
  const annulla = useMutation({
    meta: {
      fatto: (d) => {
        const n = (d as { cancelled: number }).cancelled;
        return n === 1
          ? '1 prenotazione annullata per il campo non utilizzabile'
          : `${n} prenotazioni annullate per il campo non utilizzabile`;
      },
    },
    mutationFn: () => chiama<{ cancelled: number }>('POST', '/courts/unusable', corpo()),
    onSuccess: (r) => {
      setFatto(r.cancelled);
      setColpite(null);
      void qc.invalidateQueries({ queryKey: ['calendario'] });
    },
  });
  const cambia =
    <T,>(f: (v: T) => void) =>
    (v: T) => {
      setColpite(null);
      setFatto(null);
      f(v);
    };

  return (
    <Pannello etichetta="Campo non utilizzabile" chiudi={chiudi}>
      <TestaPannello
        titolo="🌧️ Campo non utilizzabile"
        sotto="Guasto, maltempo, un evento: le prenotazioni si annullano, e chi era in campo lo sa dal bot."
        chiudi={chiudi}
      />
      <div className="corpo-pannello">
        <div className="scelte-caparra">
          <strong>Campi</strong>
          {campi.map((c) => (
            <label key={c.id}>
              <input
                type="checkbox"
                checked={scelti.includes(c.id)}
                onChange={(e) =>
                  cambia(setScelti)(e.target.checked ? [...scelti, c.id] : scelti.filter((x) => x !== c.id))
                }
              />
              {c.name}
            </label>
          ))}
        </div>
        <label className="campo">
          <span>Giorno</span>
          <input type="date" value={giorno} onChange={(e) => cambia(setGiorno)(e.target.value)} />
        </label>
        <label className="campo">
          <span>Dalle</span>
          <input type="time" value={dalle} onChange={(e) => cambia(setDalle)(e.target.value)} />
        </label>
        <label className="campo">
          <span>Alle</span>
          <input type="time" value={alle} onChange={(e) => cambia(setAlle)(e.target.value)} />
        </label>
        <label className="campo">
          <span>Motivo (lo leggono i soci)</span>
          <input
            placeholder="Guasto alle luci"
            value={motivo}
            onChange={(e) => cambia(setMotivo)(e.target.value)}
          />
        </label>
        <Guasto errore={guarda.error ?? annulla.error} />
        {fatto !== null && (
          <p className="avvertenza">
            {fatto === 0
              ? 'Non c’era niente da annullare.'
              : `Annullate ${fatto} prenotazioni: chi era in campo è avvisato.`}
          </p>
        )}
        {colpite === null ? (
          <button type="button" disabled={!pronto || guarda.isPending} onClick={() => guarda.mutate()}>
            {guarda.isPending ? 'Un attimo…' : 'Guarda quali prenotazioni tocca'}
          </button>
        ) : colpite.length === 0 ? (
          <p className="tenue">In quelle ore, su quei campi, non c’è nessuna prenotazione.</p>
        ) : (
          <>
            <table className="tabella">
              <thead>
                <tr>
                  <th>Prenotazione</th>
                  <th>Organizzatore</th>
                </tr>
              </thead>
              <tbody>
                {colpite.map((c) => (
                  <tr key={c.bookingId}>
                    <td>
                      {c.time} · {c.courtName}
                    </td>
                    <td>{c.organizer ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="azioni-caparra">
              <button type="button" disabled={annulla.isPending} onClick={() => annulla.mutate()}>
                {annulla.isPending ? 'Un attimo…' : `Annulla le ${colpite.length} prenotazioni e avvisa`}
              </button>
              <button type="button" className="bordato" onClick={() => setColpite(null)}>
                Lascia stare
              </button>
            </div>
          </>
        )}
      </div>
    </Pannello>
  );
}
